/**
 * SiteAdminScheduleInspector — Schedule sub-tab of Tournament Templates.
 * iter84.26.
 *
 * Site-admin diagnostic + repair tool for live schedules. The league
 * admin's Schedule tab was removed, so platform owners are the only
 * operator who can resolve a stuck-score situation. Three actions:
 *   1. Pick a rivalry tournament that uses this template (dropdown).
 *   2. Resync that tournament from its live feed (ESPN or API-Football).
 *   3. Manually override any match's home/away score (god-mode).
 *
 * Below the rivalry inspector lives the existing template-level
 * preview (kept in the parent SiteAdminTournaments). This component
 * is purely the rivalry-inspector slice.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    RefreshCw, AlertCircle, Radio, Pencil, Check, X,
} from "lucide-react";


function cacheAgeLabel(synced_at) {
    if (!synced_at) return null;
    const ageMs = Date.now() - new Date(synced_at).getTime();
    const min = Math.round(ageMs / 60000);
    if (min < 1) return { text: "just now", stuck: false };
    if (min < 60) return { text: `${min} min ago`, stuck: min > 30 };
    const hr = (min / 60).toFixed(1);
    return { text: `${hr} h ago`, stuck: true };
}


function MatchRow({ match, onOverrideSaved }) {
    const [editing, setEditing] = useState(false);
    const [scoreA, setScoreA] = useState(String(match.score_a ?? ""));
    const [scoreB, setScoreB] = useState(String(match.score_b ?? ""));
    const [busy, setBusy] = useState(false);

    const startEdit = () => {
        setScoreA(String(match.score_a ?? "0"));
        setScoreB(String(match.score_b ?? "0"));
        setEditing(true);
    };
    const cancel = () => setEditing(false);
    const save = async () => {
        const a = Number(scoreA);
        const b = Number(scoreB);
        if (Number.isNaN(a) || Number.isNaN(b) || a < 0 || b < 0) {
            toast.error("Scores must be non-negative integers");
            return;
        }
        setBusy(true);
        try {
            const data = await api.siteOverrideMatch(match.id, { score_a: a, score_b: b });
            toast.success(`Override saved: ${match.team_a_name} ${a}–${b} ${match.team_b_name}`);
            setEditing(false);
            onOverrideSaved?.(data.match);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Override failed");
        }
        setBusy(false);
    };

    const kickoff = match.kickoff ? new Date(match.kickoff).toLocaleString() : "—";
    const stageLabel = (match.stage || "").toUpperCase();

    return (
        <tr data-testid={`sched-match-row-${match.id}`} className="border-b border-[#181818] last:border-b-0 align-middle">
            <td className="py-1.5 pr-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap">{kickoff}</td>
            <td className="py-1.5 pr-2 text-[10px] font-mono text-cyber">{stageLabel}</td>
            <td className="py-1.5 pr-2 text-white text-right truncate">
                <span className="mr-1">{match.team_a_flag || ""}</span>{match.team_a_name}
            </td>
            <td className="py-1.5 px-2 text-center font-mono">
                {editing ? (
                    <div className="flex items-center justify-center gap-1">
                        <input
                            data-testid={`sched-score-a-input-${match.id}`}
                            type="number"
                            min="0"
                            value={scoreA}
                            onChange={(e) => setScoreA(e.target.value)}
                            className="brutal-input w-12 py-0.5 text-center text-xs"
                        />
                        <span className="text-zinc-500">–</span>
                        <input
                            data-testid={`sched-score-b-input-${match.id}`}
                            type="number"
                            min="0"
                            value={scoreB}
                            onChange={(e) => setScoreB(e.target.value)}
                            className="brutal-input w-12 py-0.5 text-center text-xs"
                        />
                    </div>
                ) : (
                    <span className={match.played ? "text-white font-bold" : "text-zinc-600"}>
                        {match.played ? `${match.score_a}–${match.score_b}` : "—"}
                    </span>
                )}
            </td>
            <td className="py-1.5 pl-2 text-white truncate">
                {match.team_b_name}<span className="ml-1">{match.team_b_flag || ""}</span>
            </td>
            <td className="py-1.5 pl-2 text-right whitespace-nowrap">
                {editing ? (
                    <>
                        <button
                            data-testid={`sched-override-save-${match.id}`}
                            onClick={save}
                            disabled={busy}
                            className="brutal-btn-ghost px-1.5 py-1 text-cyber disabled:opacity-50"
                            title="Save override"
                        >
                            {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </button>
                        <button
                            data-testid={`sched-override-cancel-${match.id}`}
                            onClick={cancel}
                            disabled={busy}
                            className="brutal-btn-ghost px-1.5 py-1 text-zinc-500 disabled:opacity-50"
                            title="Cancel"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </>
                ) : (
                    <button
                        data-testid={`sched-override-btn-${match.id}`}
                        onClick={startEdit}
                        className="brutal-btn-ghost px-1.5 py-1 text-blaze"
                        title="Manual override"
                    >
                        <Pencil className="w-3 h-3" />
                    </button>
                )}
            </td>
        </tr>
    );
}


export default function SiteAdminScheduleInspector({ templateKey }) {
    const [usage, setUsage] = useState({ tournaments: [] });
    const [usageLoading, setUsageLoading] = useState(false);
    const [selectedTid, setSelectedTid] = useState("");
    const [schedule, setSchedule] = useState(null);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [resyncBusy, setResyncBusy] = useState(false);

    // Load the list of rivalries using this template whenever the
    // template selector changes upstream. Reset the picked rivalry
    // because its tournament may no longer be valid.
    const loadUsage = useCallback(async () => {
        if (!templateKey) return;
        setUsageLoading(true);
        try {
            const data = await api.siteListTemplateUsage(templateKey);
            setUsage(data);
            // Auto-pick the first one if none selected yet — gives the
            // operator something to look at instantly.
            if (data.tournaments?.length && !data.tournaments.find(t => t.tournament_id === selectedTid)) {
                setSelectedTid(data.tournaments[0].tournament_id);
            } else if (!data.tournaments?.length) {
                setSelectedTid("");
                setSchedule(null);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load template usage");
        }
        setUsageLoading(false);
        // selectedTid intentionally excluded — it's SET inside this callback
        // (auto-pick first rivalry); depending on it would re-fetch the
        // template usage list every time the operator picks a different
        // rivalry from the dropdown instead of only when the template changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateKey]);
    useEffect(() => { loadUsage(); }, [loadUsage]);

    const loadSchedule = useCallback(async () => {
        if (!selectedTid) { setSchedule(null); return; }
        setScheduleLoading(true);
        try {
            const data = await api.siteGetTournamentSchedule(selectedTid);
            setSchedule(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load schedule");
            setSchedule(null);
        }
        setScheduleLoading(false);
    }, [selectedTid]);
    useEffect(() => { loadSchedule(); }, [loadSchedule]);

    const handleResync = async () => {
        if (!selectedTid) return;
        setResyncBusy(true);
        try {
            const r = await api.siteResyncTournamentFromFeed(selectedTid);
            if (r.feed === "api_football") {
                toast.success(`API-Football resync: ${r.played_count} played / ${r.match_count} matches`);
            } else {
                toast.success(`ESPN resync: ${r.imported_count} updated`);
            }
            await loadSchedule();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Resync failed");
        }
        setResyncBusy(false);
    };

    const onMatchPatched = (patched) => {
        // Patch in-place to avoid a full reload — the override returns
        // the updated match doc.
        setSchedule(prev => prev ? {
            ...prev,
            matches: prev.matches.map(m => m.id === patched.id ? { ...m, ...patched } : m),
        } : prev);
    };

    const cacheAge = cacheAgeLabel(schedule?.cache_info?.synced_at);

    return (
        <div className="space-y-3" data-testid="sched-inspector-card">
            <div className="brutal-card p-4 space-y-3 border-cyber/30">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2">
                        <Radio className="w-4 h-4 text-cyber" /> Rivalry schedule inspector
                    </h3>
                    {usage.tournaments.length > 0 && (
                        <p className="text-[10px] text-zinc-500">
                            {usage.tournaments.length} rivalry tournament{usage.tournaments.length === 1 ? "" : "s"} using this template
                        </p>
                    )}
                </div>
                <p className="text-xs text-zinc-400">
                    Pick a rivalry to inspect its live schedule. Use <strong className="text-white">Resync</strong> to
                    re-pull from the feed when scores look stale, or click the pencil on any match for a
                    <strong className="text-white"> manual override</strong> (god-mode — bypasses the feed entirely).
                </p>

                {usageLoading ? (
                    <p className="text-xs text-zinc-500 italic">Loading rivalries…</p>
                ) : usage.tournaments.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">
                        No rivalries currently use this template. Once one is created, it will appear here.
                    </p>
                ) : (
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[260px]">
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Rivalry · tournament</label>
                            <select
                                data-testid="sched-rivalry-picker"
                                value={selectedTid}
                                onChange={(e) => setSelectedTid(e.target.value)}
                                className="brutal-input w-full text-xs"
                            >
                                {usage.tournaments.map(t => (
                                    <option key={t.tournament_id} value={t.tournament_id}>
                                        {t.league_name} · {t.tournament_name}
                                        {t.completion_authorized_by ? " · COMPLETED" : ""}
                                        {t.has_draft ? " · drafted" : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            data-testid="sched-resync-btn"
                            onClick={handleResync}
                            disabled={!selectedTid || resyncBusy || !!schedule?.tournament?.completion_authorized_by}
                            className="brutal-btn-secondary text-xs py-2 disabled:opacity-50"
                            title={schedule?.tournament?.completion_authorized_by ? "Tournament is completed — reopen first." : "Resync this tournament from its live feed"}
                        >
                            {resyncBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Resync from feed
                        </button>
                    </div>
                )}

                {/* Cache-age + tournament-status pills */}
                {schedule && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="sched-meta-pills">
                        {cacheAge && (
                            <span
                                data-testid="sched-cache-age-pill"
                                className={`pill text-[10px] ${cacheAge.stuck ? "bg-heat/20 border-heat text-heat" : "bg-cyber/10 border-cyber/40 text-cyber"}`}
                                title={cacheAge.stuck ? "Live feed cache hasn't refreshed recently — resync or check the poller." : "Cache is fresh"}
                            >
                                {cacheAge.stuck && <AlertCircle className="w-3 h-3" />}
                                feed cache · {cacheAge.text}
                            </span>
                        )}
                        <span className="pill text-[10px] bg-ink border-[#333] text-zinc-300">
                            status · {schedule.tournament.status}
                        </span>
                        <span className="pill text-[10px] bg-ink border-[#333] text-zinc-300">
                            {schedule.matches.length} matches · {schedule.tournament.team_count} teams
                        </span>
                    </div>
                )}
            </div>

            {/* Match list */}
            {scheduleLoading ? (
                <p className="text-xs text-zinc-500 italic px-1">Loading schedule…</p>
            ) : schedule && schedule.matches.length > 0 ? (
                <div className="brutal-card p-3" data-testid="sched-matches-card">
                    <div className="max-h-[28rem] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-ink z-10">
                                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#222]">
                                    <th className="py-2 pr-2">Kickoff</th>
                                    <th className="py-2 pr-2">Stage</th>
                                    <th className="py-2 pr-2 text-right">Home</th>
                                    <th className="py-2 px-2 text-center">Score</th>
                                    <th className="py-2 pl-2">Away</th>
                                    <th className="py-2 pl-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedule.matches.map(m => (
                                    <MatchRow key={m.id} match={m} onOverrideSaved={onMatchPatched} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : schedule && schedule.matches.length === 0 ? (
                <p className="text-xs text-zinc-500 italic px-1">This tournament has no matches yet — try Resync.</p>
            ) : null}
        </div>
    );
}
